import {
  Link as ChakraLink,
  ListItem,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Text,
  UnorderedList,
  VStack,
} from '@chakra-ui/react'
import { Fragment } from 'react'
import { Trans } from 'react-i18next'
import { Link as RouterLink } from 'react-router-dom'
import { useClient } from '../core/client/ClientProvider'
import { useClientText } from '../hooks/utility/useClientText'
import { useLanguage } from '../hooks/utility/useLanguage'

// Generic information pop-out — client-agnostic.
// Texts are driven by feature flags and use {{companyName}} for the
// company name. Same source as the vanilla agent name in bootstrap.py — keeps
// a single source of truth for the assistant identity across the stack.
interface GeneralInformationModalProps {
  isOpen: boolean
  onClose: () => void
}

const GeneralInformationModal: React.FC<GeneralInformationModalProps> = ({ isOpen, onClose }) => {
  const { t, currentLanguage } = useLanguage()
  const { hasFeature, config } = useClient()
  const { companyName } = useClientText()

  const interpolation = { companyName: config?.theme.branding.assistantName || companyName }
  const support = config?.theme.support
  const dataVisibilityNotice = config?.theme.dataVisibilityNotice

  // When the client supplies its own modal content, render it verbatim for the
  // active language (falling back to English) instead of the generic copy.
  const informationModalOverride =
    config?.theme.informationModal?.[currentLanguage] ?? config?.theme.informationModal?.en

  const showVanillaChat = hasFeature('vanilla_chat')
  const showVanillaInactiveWebSearch = hasFeature('vanilla_chat') && !hasFeature('internet_search')
  const showInternalSearch = hasFeature('internal_search')
  const showInternetSearch = hasFeature('internet_search')
  const showDocumentUpload = hasFeature('document_upload')
  const showAgents = hasFeature('agents')
  const showModelSelection = hasFeature('model_selection')

  return (
    <Modal isOpen={isOpen} onClose={onClose} size={{ base: 'full', md: '3xl' }}>
      <ModalOverlay backdropFilter='blur(4px)' />
      <ModalContent>
        <ModalHeader>
          {informationModalOverride
            ? informationModalOverride.title
            : t('informationModal.title', interpolation)}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6}>
          {informationModalOverride ? (
            <VStack align='stretch' spacing={4} fontSize='sm'>
              <Text>{informationModalOverride.intro}</Text>
              {informationModalOverride.sections.map((section, sectionIndex) => (
                <Fragment key={sectionIndex}>
                  <Text fontWeight='semibold'>{section.heading}</Text>
                  <UnorderedList spacing={2}>
                    {section.items.map((item, itemIndex) => (
                      <ListItem key={itemIndex}>{item}</ListItem>
                    ))}
                  </UnorderedList>
                </Fragment>
              ))}
              {informationModalOverride.notes?.map((note, noteIndex) => (
                <Text key={noteIndex}>{note}</Text>
              ))}
            </VStack>
          ) : (
          <VStack align='stretch' spacing={4} fontSize='sm'>
            <Text>{t('informationModal.intro', interpolation)}</Text>

            <Text fontWeight='semibold'>
              {t('informationModal.capabilitiesTitle', interpolation)}
            </Text>
            <UnorderedList spacing={2}>
              {showVanillaChat && (
                <ListItem>{t('informationModal.activeVanillaChat', interpolation)}</ListItem>
              )}
              {showVanillaInactiveWebSearch && (
                <ListItem>
                  {t('informationModal.activeVanillaInactiveWebSearch', interpolation)}
                </ListItem>
              )}
              {showInternalSearch && (
                <ListItem>{t('informationModal.activeInternalSearch', interpolation)}</ListItem>
              )}
              {showInternetSearch && (
                <ListItem>
                  {t('informationModal.activeInternetSearch', interpolation)}
                  <Text mt={1} color='orange.600'>
                    {t('informationModal.activeInternetSearchPrivacyWarning', interpolation)}
                  </Text>
                </ListItem>
              )}
              {showDocumentUpload && (
                <ListItem>{t('informationModal.activeDocumentUpload', interpolation)}</ListItem>
              )}
              {showAgents && (
                <ListItem>
                  <Trans
                    i18nKey='informationModal.activeAgents'
                    values={interpolation}
                    components={{
                      1: (
                        <RouterLink
                          to='/library'
                          style={{ color: 'var(--chakra-colors-blue-500)', textDecoration: 'underline' }}
                          onClick={onClose}
                        />
                      ),
                    }}
                  />
                </ListItem>
              )}
              {showModelSelection && (
                <ListItem>
                  <Trans
                    i18nKey='informationModal.activeModelSelection'
                    values={interpolation}
                    components={{
                      1: (
                        <RouterLink
                          to='/settings?tab=models'
                          style={{ color: 'var(--chakra-colors-blue-500)', textDecoration: 'underline' }}
                          onClick={onClose}
                        />
                      ),
                    }}
                  />
                </ListItem>
              )}
            </UnorderedList>

            <Text fontWeight='semibold'>
              {t('informationModal.usageTipsTitle', interpolation)}
            </Text>
            <UnorderedList spacing={2}>
              <ListItem>{t('informationModal.askClearQuestions', interpolation)}</ListItem>
              <ListItem>{t('informationModal.conversationHistory', interpolation)}</ListItem>
              <ListItem>{t('informationModal.startNewConversation', interpolation)}</ListItem>
            </UnorderedList>

            <Text>{t('informationModal.bewareAssistantsNotAgents', interpolation)}</Text>
            <Text>{t('informationModal.feedback', interpolation)}</Text>

            {dataVisibilityNotice && (
              <>
                <Text fontWeight='semibold'>{t('informationModal.dataVisibilityNoticeTitle')}</Text>
                <Text>{dataVisibilityNotice}</Text>
              </>
            )}

            {support && (
              <>
                <Text fontWeight='semibold'>{t('informationModal.supportTitle')}</Text>
                {support.intro && <Text>{support.intro}</Text>}
                {support.items && support.items.length > 0 && (
                  <UnorderedList spacing={2}>
                    {support.items.map((item, idx) => (
                      <ListItem key={idx}>
                        {item.href ? (
                          <ChakraLink href={item.href} isExternal color='blue.500' textDecoration='underline'>
                            {item.label}: {item.value}
                          </ChakraLink>
                        ) : (
                          <>
                            {item.label}: {item.value}
                          </>
                        )}
                      </ListItem>
                    ))}
                  </UnorderedList>
                )}
              </>
            )}
          </VStack>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  )
}

export default GeneralInformationModal
